import {useState} from 'react';

import {usePeopleSearch} from '../../../shared/api/usePublicBallot';
import type {PublicPerson} from '../../../shared/ballotTypes';
import {Avatar} from '../../../shared/components/avatar';
import {PaperButton} from '../../../shared/sticker/stickers';
import {NAME_STORAGE_KEY} from '../ballotState';

const storedName = (): string => {
    try {
        return localStorage.getItem(NAME_STORAGE_KEY) ?? '';
    } catch {
        return '';
    }
};

export const NameStep = ({
    code,
    title,
    joining,
    failed,
    onJoin,
}: {
    code: string;
    title: string;
    joining: boolean;
    failed: boolean;
    onJoin: (body: {name: string; humaans_id?: string}) => void;
}) => {
    const [text, setText] = useState(storedName);
    const [picked, setPicked] = useState<PublicPerson | null>(null);
    const people = usePeopleSearch(code, text);
    const typed = text.trim();
    const suggestions = typed.length >= 2 ? (people.data ?? []) : [];

    const join = () => {
        const name = picked?.name ?? typed;
        try {
            localStorage.setItem(NAME_STORAGE_KEY, name);
        } catch {
            // Storage can be blocked; the name just is not remembered.
        }
        onJoin(picked ? {name, humaans_id: picked.humaans_id} : {name});
    };

    return (
        <main className="ph-page">
            <div className="ph-stack">
                <p className="ph-tag">{title}</p>
                <h1 className="ph-title">What's your name?</h1>
                <input
                    className="bt-field ph-field"
                    value={text}
                    maxLength={60}
                    autoComplete="off"
                    aria-label="Your name"
                    onChange={(e) => {
                        setText(e.target.value);
                        setPicked(null);
                    }}
                />
                {typed.length > 0 && (
                    <ul className="ph-suggestions">
                        {suggestions.map((person) => (
                            <li key={person.humaans_id}>
                                <button
                                    type="button"
                                    className="ph-suggestion"
                                    aria-pressed={picked?.humaans_id === person.humaans_id}
                                    onClick={() => setPicked(person)}
                                >
                                    <Avatar
                                        src={person.photo_url ?? undefined}
                                        name={person.name}
                                        size={32}
                                    />
                                    <b>{person.name}</b>
                                </button>
                            </li>
                        ))}
                        <li>
                            <button
                                type="button"
                                className="ph-suggestion ph-suggestion--typed"
                                aria-pressed={picked === null}
                                onClick={() => setPicked(null)}
                            >
                                Use "{typed}" as typed
                            </button>
                        </li>
                    </ul>
                )}
                {failed && (
                    <p className="ph-error" role="alert">
                        Could not join. Try again.
                    </p>
                )}
                <PaperButton
                    className="ph-wide"
                    disabled={typed.length === 0 || joining}
                    onClick={join}
                >
                    That's me
                </PaperButton>
            </div>
        </main>
    );
};
